import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, ArrowLeft } from 'lucide-react';
import { useLayout } from '../../contexts/layout-context';
import { Button } from '../ui/button';
import { DashboardMenubar } from '../menubar/menubar';
import { useSelector } from 'react-redux';
import { getUserStateActiveClient } from 'src/redux/user/user.selectors';

export interface NavbarTab {
  id: string;
  label: string;
  key: string;
  onClick?: () => void;
}

interface AppNavbarProps {
  showBackButton?: boolean;
  backPath?: string;
  tabs?: NavbarTab[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  children?: React.ReactNode;
}

/** Stable default so `tabs` dependency is not a new `[]` every render when omitted. */
const EMPTY_NAVBAR_TABS: NavbarTab[] = [];

export const AppNavbar: React.FC<AppNavbarProps> = ({
  showBackButton = false,
  backPath,
  tabs: tabsProp = EMPTY_NAVBAR_TABS,
  activeTab,
  onTabChange,
  children,
}) => {
  const navigate = useNavigate();
  const { isMobile, mobileSidebarOpen, setMobileSidebarOpen, sidebarCollapsed } = useLayout();
  const activeClient = useSelector(getUserStateActiveClient);

  const filteredTabs = useMemo(() => {
    if (activeClient) {
      return tabsProp.filter((tab) =>
        activeClient.permissions.some((permission) => permission.tab === tab.key || tab.key === 'ALL')
      );
    }
    return tabsProp.filter((tab) => tab.key !== 'NEVER');
  }, [activeClient, tabsProp]);

  const handleBackClick = () => {
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  const handleMenuClick = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  return (
    <nav
      className={`fixed top-0 right-0 h-16 bg-white border-b border-gray-200 z-30 transition-all duration-300 ${
        isMobile ? 'left-0' : sidebarCollapsed ? 'left-16' : 'left-80'
      }`}
    >
      <div className="flex items-center justify-between h-full px-4">
        {/* Left side */}
        <div className="flex items-center gap-4">
          {/* Mobile menu button */}
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleMenuClick}
              className="h-8 w-8"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}

          {/* Back button */}
          {showBackButton && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackClick}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}

          {/* Tabs */}
          {filteredTabs.length > 0 && (
            <div className="flex items-center">
              <div className="flex items-center space-x-8">
                {filteredTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`relative px-1 py-2 text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => {
                      tab.onClick?.();
                      onTabChange?.(tab.id);
                    }}
                  >
                    {tab.label}
                    {activeTab === tab.id && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Custom content */}
          {children}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-4">
          <DashboardMenubar />
        </div>
      </div>
    </nav>
  );
};